/**
 * LE PLAN DES AEROPORTS: une aerogare a etage, un restaurant en haut, une piste
 * devant.
 *
 * TROIS MODELES, UN SEUL PLAN. `aerodrome`, `aeroport` et `aeroport_verre` ne
 * different que par leur prix et par leur allure: meme emprise, meme etage, meme
 * piste, mêmes gestes. C'est voulu et c'est ecrit ici plutot que recopie trois
 * fois — le jour ou l'un d'eux devra differer, ce sera une entree de table, pas
 * une branche dans le code (la lecon de « une regle ecrite en dur pour UN modele
 * oublie toujours les suivants »).
 *
 * CE QUE CONTIENT UN AEROPORT:
 *
 *   - Au sol, le HALL: on y entre par la porte, comme partout ailleurs. Il est
 *     ouvert, sans cloison — c'est un hall, pas un appartement.
 *   - Au niveau 1, le RESTAURANT: une grande salle, sa terrasse devant elle qui
 *     donne sur la piste, et un comptoir qui sert GRATUITEMENT (le jeu n'a pas
 *     de monnaie: voir la cabane a collations de la plage).
 *   - Devant l'aerogare, la PISTE: elle n'est PAS dans l'emprise (voir
 *     `BatimentDef.piste`), parce qu'une emprise porte des murs et une dalle, ce
 *     qu'une piste n'est pas.
 *
 * TOUS LES NOMBRES SONT DES FRACTIONS de l'emprise (11 x 8 m), et les marges
 * suivent la regle de la villa et de l'hotel: une bande de murs ne doit JAMAIS
 * toucher une autre, sinon les deux se renvoient le bonhomme un tick sur deux.
 * Avec 11 x 8:
 *
 *   - l'acrotere (0,28 m) commence a pousser vers l'interieur a 4,87 m du centre
 *     en x et 3,37 m en z (demi-emprise moins l'epaisseur moins le rayon du
 *     bonhomme);
 *   - la salle du restaurant pousse vers l'exterieur jusqu'a 4,42 m en x et
 *     3,15 m en z.
 *
 * Il reste donc 45 cm de bande franche en largeur et 22 cm en profondeur, et
 * tests/unit/aeroport.test.ts balaie toute l'emprise pour le prouver plutot que
 * de croire ce calcul.
 *
 * Module PUR: uniquement des donnees.
 */
import type { PieceEtage, PlanModeleEtage } from '../types/monde.ts'

/**
 * Emprise de l'aerogare, en metres. La MEME pour les trois modeles.
 *
 * 11 x 8, et ce n'est pas une taille choisie a l'oeil: voir `PISTE_AEROPORT`.
 * L'aerogare et sa piste se posent ENSEMBLE ou pas du tout, donc c'est le total
 * qui a ete mesure — et une aerogare de 13 x 10 coutait a elle seule un tiers
 * des emplacements du monde.
 */
export const TAILLE_AEROGARE = { l: 11, p: 8 } as const

/**
 * LA PISTE, en metres — ET C'EST UNE TAILLE MESUREE, deux fois.
 *
 * PREMIERE MESURE, sur le relief seul: une aerogare de 13 x 10 avec une piste
 * de 32 x 7 se posait a 1231 endroits du monde, davantage que l'hotel. On a
 * failli s'arreter la. C'ETAIT LE PIEGE DU ZOO, exactement: le relief n'est pas
 * ce qui encombre un monde, ce sont les ARBRES. Avec les 4595 noeuds de
 * ressource en place, la meme piste ne se posait plus que douze fois — et zero
 * fois en foret.
 *
 * DEUXIEME MESURE, arbres compris, sur huit tailles. Ce qu'elle donne:
 *
 *   aerogare 13 x 10 + piste 32 x 7 :  12 emplacements
 *   aerogare 12 x  9 + piste 26 x 6 :  33
 *   aerogare 11 x  8 + piste 22 x 6 :  70   <- retenu
 *   aerogare 11 x  8 + piste 20 x 6 :  85
 *
 * On retient 22 x 6: 70 emplacements, soit plus que le zoo (53) et que l'hotel
 * (42), qui sont la barre que ce projet s'est donnee. Vingt metres se poserait
 * un peu plus souvent, mais une piste doit rester UNE PISTE a l'oeil.
 *
 * La mecanique, elle, n'a jamais eu besoin de trente metres: l'appareil quitte
 * le sol en neuf metres et s'arrete en huit (shared/sim/avion.ts), donc 22 m
 * laissent une marge des deux cotes.
 *
 * `ecart` est le TABLIER entre l'aerogare et le seuil: c'est la que les
 * appareils se garent, assez loin du batiment pour que
 * `validerPlacementVehicule` ne les refuse pas contre le mur.
 *
 * `deniveleMax` est plus genereux que celui d'un batiment (1,6 m) parce que le
 * tapis SUIT le terrain au lieu de le raser: trois metres sur vingt-deux, c'est
 * une pente qu'on lit comme un aerodrome de campagne, pas comme un tremplin.
 */
export const PISTE_AEROPORT = { longueur: 22, largeur: 6, ecart: 3, deniveleMax: 3 } as const

/**
 * LA SALLE DU RESTAURANT, au niveau 1.
 *
 * Sa porte est sur SA face -z — la convention de toutes les ouvertures du
 * projet — et elle donne sur la terrasse qui surplombe la piste: on mange en
 * regardant les avions, ce qui est le seul vrai plaisir d'un aeroport.
 */
const SALLE_RESTAURANT: PieceEtage = { x: 0, z: 0.14, dl: 0.37, dp: 0.21, porte: 1.8 }

/**
 * LE PLAN COMMUN AUX TROIS AEROPORTS.
 *
 * Un seul niveau au-dessus du sol, donc un ESCALIER (c'est `accesEtage` qui le
 * deduit du nombre de niveaux, jamais le nom du modele): trois destinations
 * demanderaient des boutons, deux se devinent d'un tap.
 */
export const PLAN_AEROPORT: PlanModeleEtage = {
  acces: 'escalier',
  /** L'escalier debouche DANS la salle, contre le mur de gauche. */
  accesPos: { x: -0.32, z: 0 },
  niveaux: [[SALLE_RESTAURANT]],
  /** Le comptoir, au fond de la salle: c'est lui qu'on touche pour manger. */
  restaurant: { x: 0.22, z: 0.28, niveau: 1 },
  nomsNiveaux: ['Hall', 'Restaurant'],
}

/** La salle du restaurant, pour le rendu et pour les tests. */
export const SALLE_AEROPORT = SALLE_RESTAURANT
